import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import apiClient from '../../../api/apiClient';
import { useToast } from '../../../context/ToastContext';

interface RoleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Department {
  id: number;
  name: string;
  code: string;
}

export default function RoleFormModal({ isOpen, onClose, onSuccess }: RoleFormModalProps) {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    division_id: '',
    status: 'active',
  });

  useEffect(() => {
    if (isOpen) {
      loadDepartments();
    }
  }, [isOpen]);

  const loadDepartments = async () => {
    try {
      const response = await apiClient.get('/hris/organization');
      setDepartments(response.departments || []);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Auto-generate code when name changes
    if (field === 'name' && value) {
      generateCode(value);
    }
  };

  const generateCode = async (name: string) => {
    try {
      const response = await apiClient.get(
        `/hris/organization/roles/generate-code?name=${encodeURIComponent(name)}`
      );
      if (response.success) {
        setFormData(prev => ({ ...prev, code: response.code }));
      }
    } catch (error) {
      console.error('Error generating code:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError('Nama role harus diisi');
      return;
    }

    if (!formData.division_id) {
      showError('Divisi harus dipilih');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        name: formData.name,
        code: formData.code,
        description: formData.description,
        division_id: parseInt(formData.division_id) || null,
        status: formData.status,
      };

      await apiClient.post('/hris/organization/roles', submitData);
      showSuccess('Role berhasil ditambahkan');
      onSuccess();
      onClose();
      setFormData({ name: '', code: '', description: '', division_id: '', status: 'active' });
    } catch (error: any) {
      showError(error.message || 'Gagal menambahkan role');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setFormData({ name: '', code: '', description: '', division_id: '', status: 'active' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Tambah Role Baru</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="division_id">
              Divisi <span className="text-red-500">*</span>
            </Label>
            <select
              id="division_id"
              value={formData.division_id}
              onChange={e => handleChange('division_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
              required
            >
              <option value="">Pilih Divisi</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>
                  {dept.name} ({dept.code})
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">
              Nama Role <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={e => handleChange('name', e.target.value)}
              placeholder="Masukkan nama role"
              className="focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">Kode Role</Label>
            <Input
              id="code"
              type="text"
              value={formData.code}
              onChange={e => handleChange('code', e.target.value)}
              placeholder="Kode akan dibuat otomatis"
              className="focus:outline-none focus:border-blue-500"
            />
            <p className="text-xs text-gray-500">Kode akan dibuat otomatis dari nama role</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Deskripsi</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={e => handleChange('description', e.target.value)}
              placeholder="Masukkan deskripsi role (opsional)"
              className="focus:outline-none focus:border-blue-500 min-h-[80px]"
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={loading}
            >
              Batal
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2"
              disabled={loading}
            >
              <Save className="w-4 h-4" />
              {loading ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
